
import * as React from 'react';
import { Link } from "react-router-dom";
import './style.scss';
import { AlpacaAccount, AlpacaPosition, PortfolioHistory, AlpacaOrder } from '../../reducers/positionSlice';
import { TableComponent } from '../../components/table';
import { DashboardComponent } from '../../components/dashboard';
import { calculatePercentage, formateTimestamp, getFormattedDate } from '../../utils/utils';
import { ChartComponent } from '../../components/chart';
import { Bar } from '../../reducers/symbolSlice';

interface LandingPageProps {
	chartPeriod: string;
	spyChartPeriod: string;
	positions: Array<AlpacaPosition>;
	account: AlpacaAccount;
	history: PortfolioHistory;
	spyBars: Bar[];
	updateBars: (period: string) => void;
	activePositions: number;
	totalTrades: number;
	orders: Array<AlpacaOrder>;
}

export const LandingPage: React.FC<LandingPageProps> = ({ positions, account, history, chartPeriod, spyChartPeriod, spyBars, updateBars, activePositions, totalTrades, orders }) => {
	// Helper function to get position open date from orders
	const getPositionOpenDate = (symbol: string): string | null => {
		// Find all buy orders for this symbol
		const buyOrders = orders.filter((o: AlpacaOrder) => o.symbol === symbol && o.side === 'buy');
		if (buyOrders.length > 0) {
			// Sort by transaction_time ascending to get the earliest
			const sortedBuyOrders = buyOrders.sort((a: AlpacaOrder, b: AlpacaOrder) => 
				new Date(a.transaction_time).getTime() - new Date(b.transaction_time).getTime()
			);
			return sortedBuyOrders[0].transaction_time;
		}
		return null;
	};

	// Filter positions to only include those with an opened date
	const filteredPositions = positions.filter(pos => getPositionOpenDate(pos.symbol) !== null);

	const columns = [{
		key: 'symbol',
		label: 'Symbol',
		renderer: (row: AlpacaPosition) => {
			return <Link to={`/symbol/${row.symbol}`}><b>{row.symbol}</b></Link>;
			// return <a target='blank' href={`https://finance.yahoo.com/quote/${row.symbol}`}><b>{row.symbol}</b></a>;
		} 
	}, {
		key: 'opened_date',
		label: 'Opened Date',
		renderer: (row: AlpacaPosition) => {
			const openDate = getPositionOpenDate(row.symbol);
			return openDate ? getFormattedDate(new Date(openDate)) : 'N/A';
		}
	}, {
		key: 'qty',
		label: 'Qty',
		renderer: (row: AlpacaPosition) => {
			return `${parseFloat(row.qty).toFixed(2)}`;
		} 
	}, {
		key: 'avg_entry_price',
		label: 'Buy Price',
		renderer: (row: AlpacaPosition) => {
			return `$${row.avg_entry_price}`;
		} 
	}, {
		key: 'current_price',
		label: 'Current Price',
		renderer: (row: AlpacaPosition) => {
			return `$${row.current_price}`;
		} 
	}, /*{
		key: 'maxPrice',
		label: 'Max Price',
		renderer: (row: AlpacaPosition) => {
			return `$${row.maxPrice}`;
		} 
	}, {
		key: 'RSI',
		label: 'RSI',
		renderer: (row: AlpacaPosition) => {
			return `${row.RSIs[0]}`;
		} 
	},*/ {
		key: 'change_today',
		label: 'Change Today',
		renderer: (row: AlpacaPosition) => {
			return (<div>
				{parseFloat(row.change_today) > 0 ? <i className='bx bx-caret-up green'></i> : <i className='bx bx-caret-down red'></i>}
				{`${(parseFloat(row.change_today) * 100).toFixed(2)}%`}
			</div>);
		} 
	}, {
		key: 'market_value',
		label: 'Market Value',
		renderer: (row: AlpacaPosition) => {
			return (<div className={parseFloat(row.unrealized_pl) > 0 ? 'green' : 'red'}>
				{`$${parseFloat(row.market_value).toFixed(2)}`}
			</div>);
		} 
	}, {
		key: 'unrealized_plpc',
		label: 'Overall Change',
		renderer: (row: AlpacaPosition) => {
			return (<div>
				{parseFloat(row.unrealized_pl) > 0 ? <i className='bx bx-caret-up green'></i> : <i className='bx bx-caret-down red'></i>}
				{`${(parseFloat(row.unrealized_plpc) * 100).toFixed(2)}%`}
			</div>);
		} 
	}];
	const extraMoney = 0;
	const equity = account && parseFloat(account.equity) + extraMoney;
	const lastEquity = account && parseFloat(account.last_equity) + extraMoney;
	const todayChange = account && (equity - lastEquity).toFixed(2);
	const todayChangePercent = account && calculatePercentage(equity, lastEquity);
	const cash = account && parseFloat(account.cash) + extraMoney;
	const marketValue = account ? parseFloat(account.long_market_value) : 0;
	
	// Calculate dashboard metrics (only using positions with opened dates)
	const totalPnL = filteredPositions.reduce((sum, pos) => sum + parseFloat(pos.unrealized_pl), 0);
	const winningTrades = filteredPositions.filter(pos => parseFloat(pos.unrealized_pl) > 0).length;
	const filteredActivePositions = filteredPositions.length;
	const winRate = filteredActivePositions > 0 ? winningTrades / filteredActivePositions : 0;
	
	return (
		<div className="landingPage">
			{ /* account ? <div className='header'>
				<div className='left'>
					<div className='title'>Investing</div>
					<div className='equity'>${equity}</div>
					<div className={`todayChange ${todayChangePercent > 0 ? 'green' : 'red'}`}>{todayChangePercent > 0 ? <i className='bx bx-caret-up green' ></i> : <i className='bx bx-caret-down red' ></i>}{todayChange} ({todayChangePercent}%) <span>Today</span></div>
				</div>
				<div className='left'>
					<div className='title'>Buying Power</div>
					<div className='equity'>${cash}</div>
				</div>
				<div className='clear' />
			</div> : null */ }
			{account && (
				<DashboardComponent 
					positions={filteredPositions}
					account={account}
					totalPnL={totalPnL}
					winRate={winRate}
					totalTrades={totalTrades}
					activePositions={filteredActivePositions}
				/>
			)}
			<div className='positions'>
				<div className='chartSection'>
					<div className='left'>
						{/*history && <ChartComponent symbol="My Portfolio" value={marketValue} labels={history.timestamp.map(t => (formateTimestamp(t)))} prices={history.equity.map(e => (e - cash))} currentPeriod={chartPeriod} updateChart={updateBars} /> */}
						{history && <ChartComponent symbol="My Portfolio" value={history.equity[history.equity.length - 1]} labels={history.timestamp.map(t => (formateTimestamp(t)))} prices={history.equity.map(e => (e))} currentPeriod={chartPeriod} updateChart={updateBars} /> }
					</div>
					<div className='left'>
						{history && spyBars && <ChartComponent symbol="SPY" value={spyBars[spyBars.length - 1].c} labels={spyBars.map(b => (formateTimestamp(b.t)))} prices={spyBars.map(b => b.c)} currentPeriod={spyChartPeriod} /> }
					</div>
					<div className='clear' />
				</div>
				
				<TableComponent 
					data={filteredPositions} 
					columns={columns} 
					itemsPerPage={500}
					title={<div className='title'>Positions <span>({filteredPositions.length} items)</span></div>}
				/>
			</div>
		</div>
	);
}
